// Pure colour helpers and the CSS variable reset used by the theme store

// Accepts #rgb, #rgba, #rrggbb, #rrggbbaa, rgb() and rgba() (comma or space syntax).
// Mostly transparent colours carry no usable luminance, so they count as unparseable.
export const parseColor = (value?: string): { r: number; g: number; b: number } | undefined => {
  if (!value || typeof value !== 'string') return undefined
  const input = value.trim()
  let r: number, g: number, b: number
  let a = 1
  const hexMatch = input.match(/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/)
  if (hexMatch) {
    let hex = hexMatch[1]
    if (hex.length <= 4) hex = hex.split('').map((c) => c + c).join('')
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
    if (hex.length === 8) a = parseInt(hex.slice(6, 8), 16) / 255
  } else {
    const fn = input.match(/^rgba?\(\s*([^)]+)\)$/i)
    if (!fn) return undefined
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean)
    if (parts.length < 3 || parts.length > 4) return undefined
    const channel = (part: string) =>
      part.endsWith('%') ? (parseFloat(part) / 100) * 255 : parseFloat(part)
    const [pr, pg, pb] = parts.slice(0, 3).map(channel)
    if ([pr, pg, pb].some((n) => Number.isNaN(n))) return undefined
    r = Math.min(255, Math.max(0, pr))
    g = Math.min(255, Math.max(0, pg))
    b = Math.min(255, Math.max(0, pb))
    if (parts[3] !== undefined) {
      a = parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3])
      if (Number.isNaN(a)) return undefined
    }
  }
  return a < 0.5 ? undefined : { r, g, b }
}

export const getContrastText = (color?: string): string | undefined => {
  const rgb = parseColor(color)
  if (!rgb) return undefined
  // YIQ (good approximation for perceived contrast)
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  return yiq >= 150 ? '#0b0b0c' : '#ffffff'
}

// ============================================
// RESET CSS VARIABLES
// ============================================
export const resetCSSVariables = () => {
  if (typeof window === 'undefined') return

  const root = document.documentElement

  const properties = [
    '--brand-primary', '--brand-secondary', '--neutral-base', '--accent-base',
    '--destructive-base', '--radius', '--primary', '--primary-foreground',
    '--secondary', '--secondary-foreground', '--accent', '--accent-foreground',
    '--background', '--foreground', '--card', '--card-foreground',
    '--popover', '--popover-foreground', '--input', '--border', '--ring',
    '--muted', '--muted-foreground',
    '--header-bg', '--header-text',
    '--body-bg', '--body-bg-image', '--body-bg-fill', '--body-bg-position',
    '--body-title', '--body-featured', '--body-warning', '--body-danger',
    '--hero-title', '--hero-description', '--hero-button-bg', '--hero-button-text',
    '--footer-bg', '--footer-text',
    '--login-header-bg', '--login-header-text', '--login-body-bg',
    '--login-body-title', '--login-body-description', '--login-body-background-image',
    '--login-body-background-fill', '--login-body-background-position',
    '--login-form-bg', '--login-form-border', '--login-form-text', '--login-form-forgot',
    '--login-form-input-text', '--login-form-input-bg', '--login-form-input-ring',
    '--login-form-button-bg', '--login-form-button-text',
    '--login-form-footer-bg', '--login-form-footer-text', '--login-form-register',
    '--mentoring-featured', '--progress-color', '--warning-color',
    '--mentoring-content-bg', '--mentoring-neutral-bg', '--mentoring-sidebar-bg',
    '--mentoring-module-bg', '--mentoring-module-text', '--mentoring-content-text',
    '--tool-card-bg', '--tool-card-hover', '--tool-card-border', '--tool-card-border-hover',
    '--tool-input-bg', '--tool-progress-bg', '--tool-progress-fill-start', '--tool-progress-fill-end',
    '--tool-progress-success-start', '--tool-progress-success-end',
    '--tool-success-bg', '--tool-success-border', '--tool-success-text', '--tool-success-icon',
    '--tool-error-bg', '--tool-error-border', '--tool-error-text', '--tool-error-icon',
    '--tool-warning-bg', '--tool-warning-border', '--tool-warning-text', '--tool-warning-icon',
    '--tool-info-bg', '--tool-info-border', '--tool-info-text', '--tool-info-icon',
    '--tool-btn-primary-bg', '--tool-btn-primary-hover', '--tool-btn-primary-text',
    '--tool-btn-success-bg', '--tool-btn-success-hover', '--tool-btn-success-text',
    '--tool-btn-secondary-bg', '--tool-btn-secondary-hover', '--tool-btn-secondary-text',
    '--tool-btn-disabled-bg', '--tool-btn-disabled-text',
    '--tool-selected-bg', '--tool-selected-border', '--tool-selected-text',
    '--tool-text-primary', '--tool-text-secondary', '--tool-text-muted', '--tool-text-disabled',
    '--tool-gradient-primary-start', '--tool-gradient-primary-end',
    '--tool-gradient-success-start', '--tool-gradient-success-end',
    '--tool-gradient-error-start', '--tool-gradient-error-end',
    '--lesson-contrast', '--lesson-text', '--lesson-text-2', '--lesson-text-3', '--lesson-line',
    '--lesson-main-bg', '--lesson-main-pad', '--lesson-ok'
  ]

  properties.forEach(prop => root.style.removeProperty(prop))
  root.removeAttribute('data-white-label')
  root.removeAttribute('data-theme-applied')
  root.removeAttribute('data-nav-style')
  root.removeAttribute('data-lesson-scheme')
}
